import { useRef, useState } from 'react';
import type { Period } from '@casetrace/shared';
import { useCaseRole } from '../../hooks/use-cases';
import { StoryStage } from './story-stage';
import { TimeWheel } from './time-wheel';
import { PeriodEditor } from './period-editor';
import { PeriodsPanel } from './periods-panel';
import { StoryToolbar } from './story-toolbar';
import type { StoryHighlight, StoryModel } from './story-model';
import { useScrollDriver } from './use-scroll-driver';
import { useCaseStoryPanels } from './use-case-story-panels';
import { usePreserveTimelineEpoch } from './use-preserve-timeline-epoch';
import type { TimelinePositionEngine } from './use-timeline-position';

interface CaseStoryShellProps {
  readonly caseId: string;
  readonly model: StoryModel;
  readonly periods: readonly Period[];
  readonly engine: TimelinePositionEngine;
}

export function CaseStoryShell({ caseId, model, periods, engine }: CaseStoryShellProps) {
  const { data: caseRole } = useCaseRole(caseId);
  const canWrite = caseRole === 'owner' || caseRole === 'member';
  const viewportRef = useRef<HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const panels = useCaseStoryPanels();

  usePreserveTimelineEpoch(model, engine);
  useScrollDriver(engine, viewportRef, { wheelSensitivity: 0.02, keyStep: 4 });

  return (
    <section
      ref={viewportRef}
      tabIndex={0}
      className="relative flex h-[calc(100dvh-3rem)] w-full overflow-hidden bg-base focus-visible:outline-2 focus-visible:outline-accent"
      aria-label="Tidslinje"
    >
      <StoryToolbar
        caseId={caseId}
        canWrite={canWrite}
        onNewPeriod={panels.openCreatePeriod}
        onOpenPeriods={panels.openPeriodsPanel}
      />
      <TimeWheel model={model} engine={engine} />
      <StoryStage
        model={model}
        engine={engine}
        selectedId={selectedId}
        onHoverItem={handleHoverItem}
        onClearHover={handleClearHover}
        onToggleDetail={handleToggleDetail}
      />
      {panels.periodsPanelOpen && (
        <PeriodsPanel
          caseId={caseId}
          periods={periods}
          model={model}
          engine={engine}
          canWrite={canWrite}
          onEdit={panels.openEditPeriod}
          onClose={panels.closePeriodsPanel}
        />
      )}
      {panels.periodEditorOpen && (
        <PeriodEditor
          caseId={caseId}
          period={panels.editingPeriod}
          onClose={panels.closePeriodEditor}
        />
      )}
    </section>
  );

  function handleToggleDetail(item: StoryHighlight) {
    setSelectedId((current) => (current === item.id ? null : item.id));
  }

  function handleHoverItem(item: StoryHighlight) {
    setSelectedId((current) => (current === item.id ? current : item.id));
  }

  function handleClearHover() {
    setSelectedId(null);
  }
}
